import React from 'react';
import { usePassportFormPresenter } from './PassportForm.presenter';

import { KEY_COMPONENTS } from 'components/modules/MainAnketa/MainAnketa.constants';
import { NAMES, PASSPORT_FORM_LABELS } from './PassportForm.constants';
import { VOID_FUNCTION } from 'constants';
import { InputMask } from 'utils/inputMask';
import { getMvdError } from 'components/modules/MainAnketa/MainAnketa.helpers';

import { CheckSvg, AlertSvg } from 'assets/img';
import { AnketaChevronStyled, ReactTooltipStyled, AreaInfo } from '../../../MainAnketa.styles';
import * as S from './PassportForm.styles';

export const PassportForm = ({
  persona,
  validate,
  errors,
  confidenceErrors,
  clearConfidenceErrors,
  isContentHidden = false,
  disabled = false,
  setFocusAt,
  onChange = VOID_FUNCTION,
}) => {
  const presenter = usePassportFormPresenter({
    persona,
    validate,
    errors,
    confidenceErrors,
    clearConfidenceErrors,
    isContentHidden,
    setFocusAt,
    onChange,
  });

  const { passportFormValues, personalFormValues } = presenter;

  if (!passportFormValues) {
    return null;
  }

  return (
    <S.FormGridRow id={KEY_COMPONENTS.PASSPORT}>
      <S.FormHeader
        collapsed={isContentHidden || !presenter.showMore}
        data-test={`${KEY_COMPONENTS.PASSPORT} ${
          isContentHidden || !presenter.showMore ? 'collapsed' : 'expanded'
        }`}
        onClick={presenter.onHeaderClick}
      >
        <AnketaChevronStyled className="chevron" />
        <h3>Паспорт</h3>

        {!disabled && (
          <AreaInfo>
            {presenter.shouldShowCheckIcon ? (
              <>
                <CheckSvg data-tip="" data-for="passportCheck" className="check" />
                <ReactTooltipStyled id="passportCheck" place="right">
                  <span>Проверен</span>
                </ReactTooltipStyled>
              </>
            ) : presenter.shouldShowAlertIcon ? (
              <>
                <AlertSvg data-tip="" data-for="passportAlert" className="alert" />
                <ReactTooltipStyled id="passportAlert" place="right">
                  <span>Загрузите документ</span>
                </ReactTooltipStyled>
              </>
            ) : null}
          </AreaInfo>
        )}
      </S.FormHeader>

      {!isContentHidden && presenter.showMore && (
        <>
          <S.SeriaNumber
            id={`${KEY_COMPONENTS.PASSPORT}_${NAMES.PASSPORT_SERIA_NUMBER}`}
            tabIndex="12"
            label={PASSPORT_FORM_LABELS[NAMES.PASSPORT_SERIA_NUMBER]}
            defaultValue={passportFormValues[NAMES.PASSPORT_SERIA_NUMBER]}
            val={passportFormValues[NAMES.PASSPORT_SERIA_NUMBER]}
            error={presenter.getError(
              `${KEY_COMPONENTS.PASSPORT}.${[NAMES.PASSPORT_SERIA_NUMBER]}`
            )}
            confidenceError={
              passportFormValues[NAMES.PASSPORT_SERIA_NUMBER] &&
              presenter.getConfidenceError(
                `${KEY_COMPONENTS.PASSPORT}.${[NAMES.PASSPORT_SERIA_NUMBER]}`
              )
            }
            mask={InputMask.PASSPORT_SERIA_NUMBER}
            unmask
            required
            disabled={disabled}
            onChange={presenter.changeHandlerSeriaNumber}
            onComplete={() => {
              setFocusAt(`${KEY_COMPONENTS.PASSPORT}_${[NAMES.PASSPORT_ISSUE_DATE]}_input`);
            }}
            onBlur={() =>
              presenter.validation(`${KEY_COMPONENTS.PASSPORT}.${[NAMES.PASSPORT_SERIA_NUMBER]}`)
            }
          />
          <S.IssueDate
            id={`${KEY_COMPONENTS.PASSPORT}_${NAMES.PASSPORT_ISSUE_DATE}`}
            tabIndex="13"
            label={PASSPORT_FORM_LABELS[NAMES.PASSPORT_ISSUE_DATE]}
            name={NAMES.PASSPORT_ISSUE_DATE}
            defaultValue={passportFormValues[NAMES.PASSPORT_ISSUE_DATE]}
            val={passportFormValues[NAMES.PASSPORT_ISSUE_DATE]}
            error={
              presenter.getError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUE_DATE}`) ||
              getMvdError(presenter.errors)
            }
            confidenceError={
              passportFormValues[NAMES.PASSPORT_ISSUE_DATE] &&
              presenter.getConfidenceError(
                `${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUE_DATE}`
              )
            }
            mask={InputMask.DATE_BEFORE_TODAY_NEW}
            required
            disabled={disabled}
            onChange={presenter.changeHandler}
            onComplete={() => {
              setFocusAt(`${KEY_COMPONENTS.PASSPORT}_${NAMES.PASSPORT_ISSUER_CODE}_input`);
            }}
            onBlur={() =>
              presenter.validation(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUE_DATE}`)
            }
          />
          <S.IssuerCode
            id={`${KEY_COMPONENTS.PASSPORT}_${NAMES.PASSPORT_ISSUER_CODE}`}
            tabIndex="14"
            label={PASSPORT_FORM_LABELS[NAMES.PASSPORT_ISSUER_CODE]}
            type="fms_unit"
            defaultValue={passportFormValues[NAMES.PASSPORT_ISSUER_CODE]?.replace('-', '')}
            error={
              presenter.getError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER_CODE}`) ||
              getMvdError(presenter.errors)
            }
            confidenceError={
              passportFormValues[NAMES.PASSPORT_ISSUER_CODE] &&
              presenter.getConfidenceError(
                `${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER_CODE}`
              )
            }
            daDataClosed={true}
            mask={InputMask.PASSPORT_DEPARTMENT_CODE2}
            unmask
            required
            disabled={disabled}
            onChange={presenter.changeHandlerIssuerCode}
            onBlur={() =>
              presenter.validation(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER_CODE}`)
            }
          />
          <S.Issuer
            id={`${KEY_COMPONENTS.PASSPORT}_${NAMES.PASSPORT_ISSUER}`}
            tabIndex="15"
            label={PASSPORT_FORM_LABELS[NAMES.PASSPORT_ISSUER]}
            type="fms_unit"
            defaultValue={passportFormValues[NAMES.PASSPORT_ISSUER]}
            error={
              presenter.getError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER}`) ||
              getMvdError(presenter.errors)
            }
            confidenceError={
              passportFormValues[NAMES.PASSPORT_ISSUER] &&
              presenter.getConfidenceError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER}`)
            }
            daDataClosed={true}
            mask={InputMask.ALL_RUS}
            required
            disabled={disabled}
            onChange={presenter.changeHandlerIssuer}
            onBlur={() =>
              presenter.validation(`${KEY_COMPONENTS.PASSPORT}.${NAMES.PASSPORT_ISSUER}`)
            }
          />
          <S.BirthdayPlace
            id={`${KEY_COMPONENTS.PASSPORT}_${NAMES.BIRTH_PLACE}`}
            tabIndex="16"
            label={PASSPORT_FORM_LABELS[NAMES.BIRTH_PLACE]}
            name={NAMES.BIRTH_PLACE}
            defaultValue={passportFormValues[NAMES.BIRTH_PLACE]}
            val={passportFormValues[NAMES.BIRTH_PLACE]}
            error={
              presenter.getError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.BIRTH_PLACE}`) ||
              getMvdError(presenter.errors)
            }
            confidenceError={
              passportFormValues[NAMES.BIRTH_PLACE] &&
              presenter.getConfidenceError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.BIRTH_PLACE}`)
            }
            mask={InputMask.ALL_RUS_BIRTH_PLACE}
            required
            disabled={disabled}
            onChange={presenter.changeHandlerBirthdayPlace}
            onBlur={() => presenter.validation(`${KEY_COMPONENTS.PASSPORT}.${NAMES.BIRTH_PLACE}`)}
          />
          <S.BirthdayDate
            id={`${KEY_COMPONENTS.PERSONAL_INFO_FORM}_${NAMES.BIRTHDAY_DATE}`}
            tabIndex="17"
            label={PASSPORT_FORM_LABELS[NAMES.BIRTHDAY_DATE]}
            name={NAMES.BIRTHDAY_DATE}
            defaultValue={personalFormValues[NAMES.BIRTHDAY_DATE]}
            val={personalFormValues[NAMES.BIRTHDAY_DATE]}
            error={
              presenter.getError(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.BIRTHDAY_DATE}`) ||
              getMvdError(presenter.errors)
            }
            confidenceError={
              personalFormValues[NAMES.BIRTHDAY_DATE] &&
              presenter.getConfidenceError(`${KEY_COMPONENTS.PASSPORT}.${NAMES.BIRTHDAY_DATE}`)
            }
            mask={InputMask.DATE_BEFORE_TODAY_CUSTOMER}
            unmask
            required
            disabled={disabled}
            onChange={presenter.changeHandlerBirthDate}
            onBlur={() =>
              presenter.validation(`${KEY_COMPONENTS.PERSONAL_INFO_FORM}.${NAMES.BIRTHDAY_DATE}`)
            }
          />
          <S.Gender
            name={NAMES.PERSON_GENDER}
            items={presenter.genderList}
            checked={
              personalFormValues[NAMES.PERSON_GENDER]
                ? personalFormValues[NAMES.PERSON_GENDER].id
                : null
            }
            disabled={disabled}
            onClick={presenter.changeHandlerGender}
          />
        </>
      )}

      {!isContentHidden && getMvdError(presenter.errors) && (
        <S.FormSubheaderPassportMVD>
          <AlertSvg />
          <span>Проверьте правильность паспортных данных. Такого паспорта не существует</span>
        </S.FormSubheaderPassportMVD>
      )}
    </S.FormGridRow>
  );
};
